import {
  CounterBaseConfig,
  CounterConfig,
  CounterType,
} from '@gripp/shared-logic';

import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { bool, mixed, number, string } from 'yup';
import { ControlledSelect } from '../../forms';
import { RoutineAdminInputText } from '../components/routineAdminInputText';
import { RoutineAdminForm, RoutineAdminFormProps } from '../types';
import { CounterDecimalSelection } from './counterDecimalSelection';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export const CounterAdminSchema = {
  uom: string().required().notOneOf(['']),
  decimal: bool().notRequired(),
  decimalPlaces: number().when('decimal', {
    is: (value: any) => value === true,
    then: () => number().required(),
    otherwise: () => number().optional(),
  }),
  counterType: mixed<CounterType>()
    .required()
    .oneOf(Object.values(CounterType)),
};

export const CounterAdminForm: RoutineAdminForm<CounterConfig> = (
  props: RoutineAdminFormProps<CounterConfig>
) => {
  return (
    <CounterAdminComponent config={props.config} control={props.control} />
  );
};

type CounterAdminComponentProps = {
  config: CounterBaseConfig;
  control: Control;
  indexPrefix?: string;
};

export const CounterAdminComponent = (props: CounterAdminComponentProps) => {
  const { t } = useTranslation();
  const i18nPrefix = 'routines.counter.admin';
  const counterTypes = useMemo(() => {
    const types: { label: string; value: CounterType }[] = [];
    const typePrefix = 'routines.counter.counterTypes';
    const enumKeys = Object.keys(CounterType).filter(
      (key) => typeof CounterType[key as keyof typeof CounterType] === 'string'
    );

    for (const key of enumKeys) {
      const value = CounterType[key as keyof typeof CounterType];
      const label = t(`${typePrefix}.${value}`);
      types.push({ label, value });
    }

    return types;
  }, [t]);

  const uomFieldName = props.indexPrefix ? `${props.indexPrefix}.uom` : 'uom';

  const decimalFieldName = props.indexPrefix
    ? `${props.indexPrefix}.decimal`
    : 'decimal';

  const decimalPlacesFieldName = props.indexPrefix
    ? `${props.indexPrefix}.decimalPlaces`
    : 'decimalPlaces';

  const counterTypeFieldName = props.indexPrefix
    ? `${props.indexPrefix}.counterType`
    : 'counterType';

  props.control.register(uomFieldName, {
    value: props.config.uom ?? '',
  });

  props.control.register(decimalFieldName, {
    value: props.config.decimal ?? false,
  });

  props.control.register(decimalPlacesFieldName, {
    value: props.config.decimalPlaces ?? undefined,
  });

  const counterTypeDefultValue =
    props.config.counterType ?? CounterType.Cumulative;

  props.control.register(counterTypeFieldName, {
    value: props.config.counterType ?? counterTypeDefultValue,
  });

  return (
    <View style={styles.container}>
      <RoutineAdminInputText
        control={props.control}
        name="uom"
        i18nPrefix={i18nPrefix}
        indexPrefix={props.indexPrefix}
      />
      <View style={styles.counterTypeContainer}>
        <Text style={styles.counterTypeTitle}>
          {t(`${i18nPrefix}.counterType`)}
        </Text>
        <ControlledSelect
          control={props.control}
          name={counterTypeFieldName}
          label={''}
          items={counterTypes}
          dropDownPickerProps={{
            scrollViewProps: {
              nestedScrollEnabled: true,
            },
            dropDownContainerStyle: [
              styles.dropdownListContainer,
              { minHeight: 100 },
            ],
          }}
        />
      </View>
      <CounterDecimalSelection
        control={props.control}
        decimalFieldName={decimalFieldName}
        decimalPlacesFieldName={decimalPlacesFieldName}
        i18nPrefix={i18nPrefix}
      />
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    justifyContent: 'flex-start',
  },
  tallyContainer: {
    flexDirection: 'row',
    marginVertical: 10,
  },
  tallyLabel: {
    fontSize: 16,
    lineHeight: 16,
    color: role.textStrong,
    fontWeight: '700',
  },
  counterTypeContainer: {
    maxWidth: 550,
    zIndex: 2,
  },
  counterTypeTitle: {
    color: role.textPrimary,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    marginBottom: 12,
  },
  dropdownListContainer: {
    position: 'relative',
    top: 0,
  },
}));
